/**
 * Consent-gated storefront-to-checkout continuity.
 *
 * Creates one opaque cart-scoped UUID, registers it as a PostHog event
 * property, and persists the same value in Shopify private cart attributes.
 */
(function installCheckoutJoin(globalObject) {
  'use strict';

  if (!globalObject?.document) return;

  const win = globalObject;
  const doc = win.document;
  const READY_EVENT = 'snowcubes:posthog-ready';
  const CONSENT_EVENT = 'visitorConsentCollected';
  const STORAGE_KEY = 'snowcubes_checkout_join_v1';
  const JOIN_ATTRIBUTE = '__sc_analytics_join';
  const ENVIRONMENT_ATTRIBUTE = '__sc_analytics_environment';
  const THEME_ATTRIBUTE = '__sc_analytics_theme_id';
  const RELEASE_ATTRIBUTE = '__sc_analytics_release_sha';
  const OWNED_MARKER = '__SC_POSTHOG_OWNED';
  // The release preparer replaces this token only in an isolated staging
  // directory. The source checkout intentionally emits no release identity.
  const BUILD_RELEASE_SHA = '__SC_RELEASE_SHA__';
  const UUID =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
  const SAFE_THEME_ID = /^[0-9]{1,30}$/;
  const SAFE_RELEASE_SHA = /^[a-f0-9]{40}$/;
  let persistedFingerprint = '';
  let desiredFingerprint = '';
  let desiredAttributes = null;
  let persistenceRunning = false;
  let postHogOwned = false;

  function analyticsAllowed() {
    try {
      return (
        win.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true
      );
    } catch (_error) {
      return false;
    }
  }

  function ownedPostHog() {
    return (
      (win.__SC_POSTHOG_READY === true ||
        win.posthog?.[OWNED_MARKER] === true) &&
      typeof win.posthog?.register === 'function'
    );
  }

  function storedJoin() {
    try {
      const value = win.sessionStorage?.getItem(STORAGE_KEY);
      return UUID.test(value || '') ? value : null;
    } catch (_error) {
      return null;
    }
  }

  function createJoin() {
    if (typeof win.crypto?.randomUUID !== 'function') return null;
    const value = win.crypto.randomUUID();
    if (!UUID.test(value)) return null;
    try {
      win.sessionStorage?.setItem(STORAGE_KEY, value);
    } catch (_error) {
      // Session persistence is best effort; the private cart attribute remains
      // the checkout-side authority for this page.
    }
    return value;
  }

  function contextAttributes(joinId) {
    const context = win.__SC_ANALYTICS_CONTEXT || {};
    const environment =
      context.theme_role === 'main' ? 'production' : 'development';
    const attributes = {
      [JOIN_ATTRIBUTE]: joinId,
      [ENVIRONMENT_ATTRIBUTE]: environment,
    };
    const themeId = String(context.theme_id || '');
    const contextReleaseSha =
      typeof context.release_sha === 'string'
        ? context.release_sha.toLowerCase()
        : '';
    const buildReleaseSha = SAFE_RELEASE_SHA.test(BUILD_RELEASE_SHA)
      ? BUILD_RELEASE_SHA.toLowerCase()
      : '';
    if (SAFE_THEME_ID.test(themeId)) attributes[THEME_ATTRIBUTE] = themeId;
    if (buildReleaseSha || SAFE_RELEASE_SHA.test(contextReleaseSha)) {
      attributes[RELEASE_ATTRIBUTE] = buildReleaseSha || contextReleaseSha;
    }
    return attributes;
  }

  function cartUpdateUrl() {
    const root =
      typeof win.Shopify?.routes?.root === 'string'
        ? win.Shopify.routes.root
        : '/';
    return `${root.endsWith('/') ? root : `${root}/`}cart/update.js`;
  }

  async function drainPersistence() {
    if (persistenceRunning) return;
    persistenceRunning = true;
    try {
      while (
        desiredAttributes &&
        persistedFingerprint !== desiredFingerprint
      ) {
        const attributes = desiredAttributes;
        const fingerprint = desiredFingerprint;
        let succeeded = false;
        try {
          const response = await win.fetch(cartUpdateUrl(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ attributes }),
            credentials: 'same-origin',
          });
          succeeded = response?.ok === true;
        } catch (_error) {
          // Checkout continuity is best effort and must never affect the cart.
        }
        if (succeeded) persistedFingerprint = fingerprint;
        if (desiredFingerprint === fingerprint && !succeeded) break;
      }
    } finally {
      persistenceRunning = false;
    }
  }

  function persist(attributes) {
    desiredAttributes = attributes;
    desiredFingerprint = JSON.stringify(attributes);
    void drainPersistence();
  }

  function activate() {
    if (!analyticsAllowed() || !ownedPostHog()) return false;
    const joinId = storedJoin() || createJoin();
    if (!joinId) return false;

    const attributes = contextAttributes(joinId);
    try {
      win.posthog.register({ analytics_join_id: joinId });
    } catch (_error) {
      return false;
    }
    postHogOwned = true;
    persist(attributes);
    return true;
  }

  function revoke() {
    try {
      if (postHogOwned && typeof win.posthog?.unregister === 'function') {
        win.posthog.unregister('analytics_join_id');
      }
    } catch (_error) {
      // Analytics must never break the storefront.
    }
    postHogOwned = false;
    try {
      win.sessionStorage?.removeItem(STORAGE_KEY);
    } catch (_error) {
      // Storage may be unavailable.
    }
    persist({
      [JOIN_ATTRIBUTE]: '',
      [ENVIRONMENT_ATTRIBUTE]: '',
      [THEME_ATTRIBUTE]: '',
      [RELEASE_ATTRIBUTE]: '',
    });
  }

  function syncConsent() {
    if (analyticsAllowed()) activate();
    else revoke();
  }

  doc.addEventListener(READY_EVENT, activate);
  doc.addEventListener(CONSENT_EVENT, syncConsent);
  syncConsent();
})(typeof window !== 'undefined' ? window : undefined);
